import React, { useMemo, useState } from "react";
import { useIsMobile } from "@/hooks/useMobile";
import { format, isToday, isTomorrow, isYesterday, getYear } from "date-fns";
import { fr } from "date-fns/locale";
import { motion } from "framer-motion";
import { Play, FlaskConical } from "lucide-react";
import { cn } from "@/lib/utils";
import { StatusBadgeV10 } from "@/components/ui/StatusBadgeV10";
import { PublicationDatePill } from "@/components/ui/publication-date-pill";
import { SignedVideoThumbnail } from "@/components/ui/SignedVideoThumbnail";
import { SignedImage } from "@/components/ui/SignedImage";
import type { CalendarEvent } from "@/services/business/calendar";

/* ─── Props ─── */
interface ContentTimelinePanelV10Props {
	readonly videos: CalendarEvent[];
	readonly onVideoClick?: (video: CalendarEvent) => void;
	readonly className?: string;
}

interface DateHeaderV10Props {
	readonly date: Date;
	readonly showYear?: boolean;
	readonly isActiveDay?: boolean;
	readonly compact?: boolean;
	readonly align?: "left" | "right";
}

interface ContentTimelineItemV10Props {
	readonly video: CalendarEvent;
	readonly onClick?: () => void;
}

/* ─── Container Animations ─── */
const containerVariants = {
	hidden: { opacity: 0 },
	show: {
		opacity: 1,
		transition: { staggerChildren: 0.06, delayChildren: 0.08 },
	},
};

const groupVariants = {
	hidden: { opacity: 0, y: 16 },
	show: {
		opacity: 1,
		y: 0,
		transition: { duration: 0.35, ease: [0, 0, 0.58, 1] as const },
	},
};

const cardVariants = {
	hidden: { opacity: 0, scale: 0.96, y: 8 },
	show: {
		opacity: 1,
		scale: 1,
		y: 0,
		transition: { duration: 0.3, ease: [0, 0, 0.58, 1] as const },
	},
};

/* ─── DateHeader ─── */
export function DateHeaderV10({
	date,
	showYear,
	isActiveDay,
	compact,
	align = "left",
}: DateHeaderV10Props) {
	const day = format(date, "d");
	const weekday = format(date, "EEEE", { locale: fr }).toUpperCase();
	const month = format(date, "MMMM", { locale: fr }).toUpperCase();
	const year = format(date, "yyyy");

	let specialLabel = "";
	if (isToday(date)) specialLabel = "AUJOURD'HUI";
	else if (isTomorrow(date)) specialLabel = "DEMAIN";
	else if (isYesterday(date)) specialLabel = "HIER";

	const isHighlighted = isToday(date);

	return (
		<div
			className={cn(
				"flex flex-col",
				align === "right" ? "items-end" : "items-start",
			)}
		>
			{/* Year above day */}
			{showYear && (
				<span className="text-[10px] font-semibold uppercase tracking-widest text-[hsl(var(--v10-text-muted))] mb-0.5">
					{year}
				</span>
			)}

			{/* Large day number */}
			<span
				className={cn(
					"tabular-nums font-extralight leading-none",
					compact ? "text-3xl" : "text-5xl",
					isHighlighted
						? "text-[hsl(var(--v10-accent))]"
						: "text-[hsl(var(--v10-text-primary))]",
				)}
			>
				{day}
			</span>

			{/* Weekday label */}
			<span
				className={cn(
					"font-semibold uppercase mt-1",
					compact
						? "text-[9px] tracking-[0.06em]"
						: "text-[11px] tracking-[0.08em] mt-1.5",
					isHighlighted
						? "text-[hsl(var(--v10-accent))]"
						: "text-[hsl(var(--v10-text-secondary))]",
				)}
			>
				{specialLabel || weekday}
			</span>

			{/* Month */}
			<span
				className={cn(
					"font-medium uppercase text-[hsl(var(--v10-text-muted))]",
					compact
						? "text-[8px] tracking-[0.04em]"
						: "text-[9px] tracking-[0.06em]",
				)}
			>
				{month}
			</span>

			{/* Golden dot */}
			{isHighlighted && (
				<motion.span
					className={cn(
						"rounded-full bg-[hsl(var(--v10-accent))] mt-1.5",
						compact ? "w-2 h-2" : "w-2.5 h-2.5 mt-2",
					)}
					initial={{ scale: 0 }}
					animate={{ scale: 1 }}
					transition={{ type: "spring", stiffness: 500, damping: 25 }}
				/>
			)}
		</div>
	);
}

/* ─── Timeline Item ─── */
export function ContentTimelineItemV10({
	video,
	onClick,
}: ContentTimelineItemV10Props) {
	const [hovered, setHovered] = useState(false);
	const isMobile = useIsMobile();
	const pub = video.currentDatePublications?.[0] ?? video.publicationTimes?.[0];

	return (
		<motion.button
			variants={cardVariants}
			whileHover={{ scale: 1.015, x: 4 }}
			whileTap={{ scale: 0.99 }}
			className={cn(
				"w-full flex items-center gap-3 rounded-xl p-2.5 text-left transition-all duration-200",
				"bg-white/[0.02] border border-[hsl(var(--v10-border))]",
				"hover:bg-white/[0.06] hover:border-[hsl(var(--v10-border-hover))]",
			)}
			onClick={onClick}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
		>
			{/* Thumbnail — static cover image preferred, fallback to video first-frame */}
			{(video.coverUrl || video.videoUrl) && (
				<div className="relative w-16 h-[88px] sm:w-20 sm:h-20 rounded-lg overflow-hidden shrink-0 bg-[hsl(var(--v10-bg-elevated))]">
					{video.coverUrl ? (
						<SignedImage
							src={video.coverUrl}
							className="w-full h-full object-cover"
						/>
					) : (
						<SignedVideoThumbnail
							src={video.videoUrl}
							className="w-full h-full object-cover"
						/>
					)}
				</div>
			)}

			{/* Content central */}
			<div className="flex-1 min-w-0 flex flex-col gap-3">
				{/* Mobile: status badge above title */}
				{video.contentStatus && isMobile && (
					<div className="flex justify-end">
						<StatusBadgeV10
							status={video.contentStatus}
							variant="icon-text"
							size="md"
							expanded
							className="shrink-0 w-fit"
						/>
					</div>
				)}

				{/* Title row + A/B badge + status (desktop) */}
				<div className="flex items-center gap-2">
					<p className="text-sm font-medium text-[hsl(var(--v10-text-primary))] break-words line-clamp-3 flex-1 min-w-0">
						{video.title}
					</p>
					{video.isMultihook && video.channels?.includes("youtube") && (
						<span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-violet-500/15 border border-violet-500/30 text-violet-400 text-[9px] font-semibold shrink-0">
							<FlaskConical className="w-2.5 h-2.5" />
							A/B
						</span>
					)}
					{video.contentStatus && !isMobile && (
						<StatusBadgeV10
							status={video.contentStatus}
							variant={hovered ? "icon-text" : "icon-only"}
							size="md"
							expanded={hovered}
							className="shrink-0 h-6"
						/>
					)}
				</div>

				{/* Publication pill */}
				{pub && (
					<div className="flex sm:justify-start justify-end">
						<PublicationDatePill
							time={pub.time}
							channels={
								video.currentDatePublications?.map((p) => p.channel) ??
								video.channels ??
								[]
							}
							size="sm"
						/>
					</div>
				)}
			</div>
		</motion.button>
	);
}

/* ─── Main Component ─── */
export const ContentTimelinePanelV10: React.FC<
	ContentTimelinePanelV10Props
> = ({ videos, onVideoClick, className }) => {
	/* Group by publication date, sorted descending */
	const groups = useMemo(() => {
		const contentVideos = videos.filter(
			(v) => v.type === "content" || v.type === "ab_testing",
		);

		const map = new Map<string, { date: Date; items: CalendarEvent[] }>();
		for (const v of contentVideos) {
			const pubDate = v.publicationTimes?.[0]?.date ?? v.date;
			const key = format(pubDate, "yyyy-MM-dd");
			if (!map.has(key)) map.set(key, { date: pubDate, items: [] });
			map.get(key)!.items.push(v);
		}

		return [...map.entries()]
			.sort(([a], [b]) => b.localeCompare(a))
			.map(([, val]) => val);
	}, [videos]);

	if (groups.length === 0) {
		return (
			<div
				className={cn(
					"flex items-center justify-center py-12 text-[hsl(var(--v10-text-tertiary))] text-sm",
					className,
				)}
			>
				Aucun contenu à afficher
			</div>
		);
	}

	const currentYear = getYear(new Date());

	return (
		<motion.div
			className={cn("flex flex-col gap-6", className)}
			variants={containerVariants}
			initial="hidden"
			animate="show"
		>
			{groups.map((group, gi) => {
				const showYear = getYear(group.date) !== currentYear;
				const prevYear = gi > 0 ? getYear(groups[gi - 1].date) : currentYear;
				const yearTransition = gi > 0 && getYear(group.date) !== prevYear;

				return (
					<motion.div
						key={format(group.date, "yyyy-MM-dd")}
						variants={groupVariants}
					>
						{yearTransition && (
							<div className="text-[11px] font-semibold uppercase tracking-widest text-[hsl(var(--v10-text-muted))] mb-3">
								{getYear(group.date)}
							</div>
						)}

						<DateHeaderV10 date={group.date} showYear={showYear} />

						{/* Vertical connector line */}
						<div className="relative pl-5 ml-2">
							<motion.div
								className="absolute left-0 top-0 bottom-0 w-px bg-[hsl(var(--v10-border))]"
								initial={{ scaleY: 0 }}
								animate={{ scaleY: 1 }}
								transition={{ duration: 0.4, delay: 0.2 }}
								style={{ transformOrigin: "top" }}
							/>

							<div className="flex flex-col gap-1">
								{group.items.map((video) => (
									<ContentTimelineItemV10
										key={video.id}
										video={video}
										onClick={() => onVideoClick?.(video)}
									/>
								))}
							</div>
						</div>
					</motion.div>
				);
			})}
		</motion.div>
	);
};
